import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone } from "lucide-react";
import { submitContact } from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message received. Concierge will respond within 24 hours.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="contact-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 space-y-10"
        >
          <div>
            <div className="label-eyebrow mb-4">Concierge</div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight leading-none">
              Speak with <span className="italic text-gradient-gold">BananaX</span>.
            </h1>
            <p className="text-mist font-light leading-relaxed mt-6">
              For product guidance, subscription support, or partnership inquiries —
              our concierge team responds within 24 hours, in confidence.
            </p>
          </div>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <Mail className="w-4 h-4 text-gold stroke-[1.4] mt-1" />
              <div>
                <div className="label-eyebrow text-mist">Email</div>
                <p className="text-bone font-light mt-1">contact@mediproexpress.com</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-4 h-4 text-gold stroke-[1.4] mt-1" />
              <div>
                <div className="label-eyebrow text-mist">WhatsApp</div>
                <p className="text-bone font-light mt-1">+91 97200 09997 · 10am–9pm IST</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="w-4 h-4 text-gold stroke-[1.4] mt-1" />
              <div>
                <div className="label-eyebrow text-mist">Atelier</div>
                <p className="text-bone font-light mt-1">Dwarka, New Delhi · India</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-7 bg-ink-900 border border-white/10 p-10 space-y-5"
          data-testid="contact-form"
        >
          <h2 className="font-serif text-3xl mb-4">Write to us</h2>
          <input
            required
            value={form.name}
            onChange={update("name")}
            placeholder="Your name"
            className="w-full bg-ink-950 border border-white/10 text-bone px-4 py-3 text-sm font-light focus:outline-none focus:border-gold"
            data-testid="contact-name-input"
          />
          <input
            required
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="Your email"
            className="w-full bg-ink-950 border border-white/10 text-bone px-4 py-3 text-sm font-light focus:outline-none focus:border-gold"
            data-testid="contact-email-input"
          />
          <textarea
            required
            value={form.message}
            onChange={update("message")}
            rows={6}
            placeholder="How may we help?"
            className="w-full bg-ink-950 border border-white/10 text-bone px-4 py-3 text-sm font-light focus:outline-none focus:border-gold resize-none"
            data-testid="contact-message-input"
          />
          <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="contact-submit-btn">
            {loading ? "Sending..." : "Send message"}
          </button>
        </motion.form>
      </div>
    </div>
  );
}
